<template>
  <div
    class="date-group"
    :class="{ 'date-group--open-top': openOnTop, 'small-content': small }"
  >
    <label
      v-if="!hideLabel"
      class="form-label"
      :for="elementId"
    >
      {{ label }}
    </label>
    <Datepicker
      :id="elementId"
      v-model="internalValue"
      bootstrap-styling
      :calendar-button="calendarButton"
      calendar-button-icon="ph-calendar"
      :clear-button="clearable"
      :disabled="disabled"
      :disabled-dates="disabledDates"
      :format="format"
      :language="dateLanguage"
      :minimum-view="minimumView"
      :placeholder="placeholder ?? ''"
      @cleared="handleDateChange"
      @closed="handleDateChange"
    />
  </div>
</template>

<script lang="ts">
import { adjustToLocalTimezone, localDateISO } from "@/globals/methods";
import { computed, onMounted, ref, watch } from "vue";
import { ptBR as dateLanguage } from "vuejs-datepicker/dist/locale";
import Datepicker from "vuejs-datepicker";
import InputValidation from "@/components/comum/input/input-validation.vue";

let idCount = 0;

export default {
  components: { Datepicker, InputValidation },
  props: {
    calendarButton: { default: true, type: Boolean },
    clearable: { type: Boolean },
    disabled: { default: false, type: Boolean },
    label: { default: "Data", type: String },
    hideLabel: { default: false, type: Boolean },
    format: { default: "dd/MM/yyyy", type: String },
    minimumView: { default: "day", type: String },
    max: {
      default: null,
      type: String,
      validator: (value: string | null) => value === null || /^\d{4}-\d{2}-\d{2}$/.test(value),
    },
    min: {
      default: null,
      type: String,
      validator: (value: string | null) => value === null || /^\d{4}-\d{2}-\d{2}$/.test(value),
    },
    value: {
      default: null,
      type: String,
      validator: (value: string | null) => value === null || /^\d{4}-\d{2}-\d{2}$/.test(value),
    },
    id: {
      default: null,
      type: String,
    },
    small: {
      default: false,
      type: Boolean,
    },
    placeholder: {
      default: null,
      type: String,
    },
    openOnTop: {
      default: false,
      type: Boolean,
    },
  },

  setup(props) {
    const internalValue = ref<Date | null>(null);

    onMounted(() => {
      setInternalValue(props.value);
    });

    let elementId;

    if (props.id) {
      elementId = props.id;
    } else {
      elementId = "string-date-picker-" + idCount;
      idCount += 1;
    }

    function setInternalValue(newValue: string | null) {
      if (newValue) {
        internalValue.value = adjustToLocalTimezone(newValue);
      } else {
        internalValue.value = null;
      }
    }

    watch(() => props.value, setInternalValue);

    const disabledDates = computed(() => {
      return {
        from: props.max ? adjustToLocalTimezone(props.max) : null,
        to: props.min ? adjustToLocalTimezone(props.min) : null,
      };
    });

    return { dateLanguage, disabledDates, internalValue, elementId };
  },

  methods: {
    handleDateChange() {
      this.$emit("input", this.internalValue === null ? null : localDateISO(this.internalValue));
    },
  },
};
</script>

<style scoped>
.date-group.small-content :deep(.input-group > input),
.date-group.small-content :deep(.input-group > span) {
  min-height: 100%;
  max-height: 25px !important;
  font-size: 0.75rem;
}

.date-group :deep(.vdp-datepicker__calendar) {
  z-index: 1080;
}

.date-group--open-top :deep(.vdp-datepicker__calendar) {
  bottom: calc(100% + 0.25rem);
  margin-top: 0;
  top: auto;
}
</style>
